import {
  AlertCircle,
  Accessibility,
  ArrowDown,
  BadgeCheck,
  CalendarCheck2,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  CreditCard,
  DoorOpen,
  HeartHandshake,
  LockKeyhole,
  Phone,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { copy } from "./i18n";
import { AccessibilityMenu } from "./components/AccessibilityMenu";
import { BookingDatePicker } from "./components/BookingDatePicker";
import { calendarLabelToIso, getBookingDateWindow, isDateInWindow } from "./lib/dates";
import { getTreatment, getTreatments, MASSAGE_RESOURCES } from "./lib/catalog";
import {
  BOOKING_POLICY_VERSION,
  BookingReservationError,
  reserveBooking,
} from "./lib/booking";
import {
  countFreeRoomWindows,
  fetchSchedule,
  formatDay,
  getAvailableStartTimes,
  getRoomBookingAvailability,
  readScheduleCache,
} from "./lib/schedule";
import type {
  BookingKind,
  Locale,
  RoomBookingAvailability,
  RoomKey,
  RoomSelection,
  RoomState,
  ScheduleDay,
  ScheduleResponse,
  TreatmentDuration,
} from "./types";

const LOGO_URL = "/restore-logo.png";
const LEGAL_LINKS = {
  terms: "https://spa-restore.co.il/terms-cancellation/",
  privacy: "https://spa-restore.co.il/privacy-policy/",
  accessibility: "https://spa-restore.co.il/accessibility-statement/",
} as const;
const REFRESH_INTERVAL_MS = 60_000;
const CLOCK_REFRESH_INTERVAL_MS = 30_000;
const MINIMUM_LEAD_MINUTES = 120;

type View = "booking" | "schedule";
type PaymentError = "availability" | "check" | "payment" | "booking" | null;

function MiniBaroqueLamp({ mirrored = false }: { mirrored?: boolean }) {
  return (
    <span
      className={`mini-baroque-lamp${mirrored ? " mini-baroque-lamp--mirrored" : ""}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 72 92" focusable="false">
        <path className="mini-lamp-hook" d="M36 2v12M36 8c-13 0-17 8-15 17 1 5 7 8 12 5 3-2 3-7 0-9" />
        <path className="mini-lamp-crown" d="M36 13c4 7 11 10 18 12-5 2-8 5-10 9H28c-2-4-5-7-10-9 7-2 14-5 18-12Z" />
        <ellipse className="mini-lamp-aura" cx="36" cy="51" rx="26" ry="29" />
        <path className="mini-lamp-frame" d="M23 31h26l5 9-4 30-14 12-14-12-4-30 5-9Zm6 7-3 5 3 23 7 7 7-7 3-23-3-5H29Z" />
        <path className="mini-lamp-glass" d="M29 38h14l3 5-3 23-7 7-7-7-3-23 3-5Z" />
        <g className="mini-lamp-flame">
          <path className="mini-flame-outer" d="M36 64c-8-7-4-17 1-24 1 6 7 8 5 15-1 5-3 8-6 9Z" />
          <path className="mini-flame-inner" d="M36 61c-3-3-1-8 1-12 1 3 3 5 3 8-1 2-2 4-4 4Z" />
        </g>
        <path className="mini-lamp-base" d="M21 69h30l-5 8H26l-5-8Zm10 9h10l-5 11-5-11Z" />
      </svg>
    </span>
  );
}

function ServiceBaroqueLights() {
  return (
    <div className="service-baroque-lights" aria-hidden="true">
      <MiniBaroqueLamp />
      <span className="service-baroque-line"><span>✦</span></span>
      <MiniBaroqueLamp mirrored />
    </div>
  );
}

function readInitialLocale(): Locale {
  const saved = localStorage.getItem("restore-booking-locale");
  return saved === "en" ? "en" : "he";
}

function isRoomSelectionAvailable(
  start: { rooms: RoomKey[] } | undefined,
  bookingKind: BookingKind | null,
  selection: RoomSelection | "",
) {
  if (!start || !bookingKind || !selection) return false;
  if (bookingKind === "couple") {
    return (
      selection === "both" &&
      start.rooms.includes("room1") &&
      start.rooms.includes("room2")
    );
  }

  return selection !== "both" && start.rooms.includes(selection);
}

function App() {
  const [initialSchedule] = useState(() =>
    readScheduleCache(getBookingDateWindow().min),
  );
  const [locale, setLocale] = useState<Locale>(readInitialLocale);
  const [view, setView] = useState<View>("booking");
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(initialSchedule);
  const [selectedDate, setSelectedDate] = useState(() => getBookingDateWindow().min);
  const [dateChosen, setDateChosen] = useState(false);
  const requestedDateRef = useRef(selectedDate);
  const requestVersion = useRef(0);
  const reservationAttemptRef = useRef<{ signature: string; requestId: string } | null>(null);
  const [isLoading, setIsLoading] = useState(!initialSchedule);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [bookingKind, setBookingKind] = useState<BookingKind | null>(null);
  const [duration, setDuration] = useState<TreatmentDuration | null>(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedRoom, setSelectedRoom] = useState<RoomSelection | "">("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<PaymentError>(null);

  const text = copy[locale];
  const selectedDay = schedule?.days.find(day => calendarLabelToIso(day.label) === selectedDate);
  const freeWindows = useMemo(
    () => countFreeRoomWindows(selectedDay),
    [selectedDay],
  );
  const availableStarts = useMemo(
    () =>
      getAvailableStartTimes(dateChosen ? selectedDay : undefined, duration, {
        bookingKind: bookingKind ?? undefined,
        minimumLeadMinutes: MINIMUM_LEAD_MINUTES,
        now,
        timeZone: schedule?.timezone ?? "Asia/Jerusalem",
      }),
    [bookingKind, dateChosen, selectedDay, duration, now, schedule?.timezone],
  );
  const treatments = getTreatments(bookingKind);
  const selectedTreatment = getTreatment(bookingKind, duration);
  const totalPrice = selectedTreatment?.price ?? null;
  const checkoutUrl = selectedTreatment?.paymentUrl ?? null;
  const formatPrice = (price: number) =>
    new Intl.NumberFormat(text.localeTag, { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(price);
  const bookingKindName = bookingKind
    ? {
        single: text.kindSingle,
        couple: text.kindCouple,
        fourHands: text.kindFourHands,
      }[bookingKind]
    : "";
  const selectedDayLabel = selectedDay
    ? formatDay(selectedDay.label, locale)
    : null;
  const phoneDigits = clientPhone.replace(/\D/g, "");
  const formReady =
    Boolean(bookingKind) &&
    Boolean(selectedTreatment) &&
    dateChosen &&
    Boolean(selectedDay) &&
    isDateInWindow(selectedDate, getBookingDateWindow(now)) &&
    Boolean(selectedTime) &&
    Boolean(selectedRoom) &&
    clientName.trim().length >= 2 &&
    phoneDigits.length >= 9;
  const currentStep = !bookingKind
    ? 1
    : !duration
      ? 2
      : !dateChosen
        ? 3
        : !selectedTime || !selectedRoom
          ? 4
          : 5;

  const load = useCallback(async (background = false, forceFresh = false, date = requestedDateRef.current || getBookingDateWindow().min) => {
    const version = ++requestVersion.current;
    if (background) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const next = await fetchSchedule(undefined, forceFresh, date);
      if (version !== requestVersion.current) return;
      setSchedule(next);
      setError(false);
    } catch {
      if (version === requestVersion.current) setError(true);
    } finally {
      if (version === requestVersion.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(Boolean(initialSchedule));
    const timer = window.setInterval(
      () => void load(true),
      REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [initialSchedule, load]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setNow(new Date()),
      CLOCK_REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = text.direction;
    localStorage.setItem("restore-booking-locale", locale);
  }, [locale, text.direction]);

  useEffect(() => {
    if (!selectedTime || !selectedRoom) return;
    const start = availableStarts.find((item) => item.time === selectedTime);
    if (!isRoomSelectionAvailable(start, bookingKind, selectedRoom)) {
      setSelectedTime("");
      setSelectedRoom("");
      setShowPreview(false);
      setPolicyAccepted(false);
    }
  }, [availableStarts, bookingKind, selectedRoom, selectedTime]);

  const updatedAt = schedule
    ? new Intl.DateTimeFormat(text.localeTag, {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: schedule.timezone,
      }).format(new Date(schedule.generatedAt))
    : null;

  function scrollToStep(id: string) {
    window.setTimeout(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  function chooseBookingKind(nextKind: BookingKind) {
    setBookingKind(nextKind);
    setDuration(null);
    setDateChosen(false);
    setSelectedTime("");
    setSelectedRoom("");
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    scrollToStep("booking-step-2");
  }

  function chooseDuration(nextDuration: TreatmentDuration) {
    setDuration(nextDuration);
    setDateChosen(false);
    setSelectedTime("");
    setSelectedRoom("");
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    scrollToStep("booking-step-3");
  }

  function chooseBookingDay(date: string) {
    if (isCheckingPayment || (date && !isDateInWindow(date, getBookingDateWindow()))) return;
    requestedDateRef.current = date;
    requestVersion.current++;
    setSelectedDate(date);
    setDateChosen(Boolean(date));
    setSelectedTime("");
    setSelectedRoom("");
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    const alreadyLoaded = schedule?.days.some(
      (day) => calendarLabelToIso(day.label) === date,
    );
    if (date && !alreadyLoaded) {
      const cached = readScheduleCache(date);
      if (cached) setSchedule(cached);
      void load(true, false, date);
    } else {
      setIsRefreshing(false);
    }
    if (date) scrollToStep("booking-step-4");
  }

  function chooseScheduleDay(date: string) {
    if (isCheckingPayment || (date && !isDateInWindow(date, getBookingDateWindow()))) return;
    requestedDateRef.current = date;
    requestVersion.current++;
    setSelectedDate(date);
    setDateChosen(false);
    setSelectedTime("");
    setSelectedRoom("");
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    const alreadyLoaded = schedule?.days.some(
      (day) => calendarLabelToIso(day.label) === date,
    );
    if (date && !alreadyLoaded) {
      const cached = readScheduleCache(date);
      if (cached) setSchedule(cached);
      void load(true, false, date);
    } else {
      setIsRefreshing(false);
    }
  }

  function chooseRoomTime(time: string, room: RoomSelection) {
    setSelectedTime(time);
    setSelectedRoom(room);
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    scrollToStep("booking-step-5");
  }

  function openView(nextView: View) {
    setView(nextView);
    setShowPreview(false);
    setPolicyAccepted(false);
    setPaymentError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openPreview() {
    if (!formReady) return;
    setPolicyAccepted(false);
    setShowPreview(true);
    window.setTimeout(() => {
      document.getElementById("booking-summary")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
  }

  async function continueToPayment() {
    if (isCheckingPayment) return;
    if (
      !formReady ||
      !policyAccepted ||
      !bookingKind ||
      !selectedTreatment ||
      !selectedDay ||
      !selectedTime ||
      !selectedRoom
    ) {
      return;
    }
    if (!checkoutUrl) {
      setPaymentError("payment");
      return;
    }

    setIsCheckingPayment(true);
    setPaymentError(null);

    try {
      const reservationSignature = JSON.stringify([
        bookingKind,
        selectedTreatment.duration,
        selectedDate,
        selectedTime,
        selectedRoom,
        clientName.trim(),
        phoneDigits,
        BOOKING_POLICY_VERSION,
      ]);
      if (reservationAttemptRef.current?.signature !== reservationSignature) {
        reservationAttemptRef.current = {
          signature: reservationSignature,
          requestId: crypto.randomUUID(),
        };
      }

      const reservation = await reserveBooking({
        requestId: reservationAttemptRef.current.requestId,
        bookingKind,
        duration: selectedTreatment.duration,
        date: selectedDate,
        time: selectedTime,
        room: selectedRoom,
        name: clientName.trim(),
        phone: clientPhone.trim(),
        locale,
        policyAccepted: true,
        policyVersion: BOOKING_POLICY_VERSION,
      });

      sessionStorage.setItem(
        "restore-booking-pending-payment",
        JSON.stringify({
          version: 6,
          createdAt: new Date().toISOString(),
          bookingReference: reservation.bookingReference,
          bookingKind,
          duration: selectedTreatment.duration,
          price: totalPrice,
          currency: "ILS",
          productCode: selectedTreatment.sku,
          quantity: 1,
          resources: MASSAGE_RESOURCES[bookingKind],
          therapistAvailabilityVerified: false,
          calendarEventCreated: true,
          calendarStatus: "pending_payment_verification",
          paymentStatus: "pending_verification",
          timezone: schedule?.timezone ?? "Asia/Jerusalem",
          date: selectedDay.label,
          isoDate: selectedDate,
          time: selectedTime,
          room: selectedRoom,
          name: clientName.trim(),
          phone: clientPhone.trim(),
          policyVersion: reservation.policyVersion,
          policyAcceptedAt: reservation.policyAcceptedAt,
        }),
      );

      window.location.assign(checkoutUrl);
    } catch (error) {
      if (
        error instanceof BookingReservationError &&
        ["slot_unavailable", "minimum_lead_time"].includes(error.code)
      ) {
        setSelectedTime("");
        setSelectedRoom("");
        setShowPreview(false);
        setPolicyAccepted(false);
        setPaymentError("availability");
      } else if (error instanceof BookingReservationError) {
        setPaymentError("booking");
      } else {
        setPaymentError("check");
      }
    } finally {
      setIsCheckingPayment(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar" id="top">
        <a className="brand" href="#top" aria-label="Restore Massage Therapy">
          <span className="logo-halo">
            {!logoFailed ? (
              <img
                src={LOGO_URL}
                alt="Restore Massage Therapy"
                className="brand-logo"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="logo-fallback">R</span>
            )}
          </span>
          <span className="brand-copy">
            <strong>Restore</strong>
            <small>Massage Therapy</small>
          </span>
        </a>

        <div className="language-switch" aria-label="Language">
          <button
            type="button"
            className={locale === "he" ? "active" : ""}
            aria-pressed={locale === "he"}
            onClick={() => setLocale("he")}
          >
            עב
          </button>
          <button
            type="button"
            className={locale === "en" ? "active" : ""}
            aria-pressed={locale === "en"}
            onClick={() => setLocale("en")}
          >
            EN
          </button>
        </div>
      </header>

      <nav className="view-tabs" aria-label={text.primaryNavigation}>
        <button
          type="button"
          className={view === "booking" ? "active" : ""}
          aria-current={view === "booking" ? "page" : undefined}
          onClick={() => openView("booking")}
        >
          <CalendarCheck2 size={20} aria-hidden="true" />
          <span>{text.bookingNav}</span>
        </button>
        <button
          type="button"
          className={view === "schedule" ? "active" : ""}
          aria-current={view === "schedule" ? "page" : undefined}
          onClick={() => openView("schedule")}
        >
          <CalendarDays size={20} aria-hidden="true" />
          <span>{text.scheduleNav}</span>
        </button>
      </nav>

      <main>
        <section className="hero hero-showcase" aria-labelledby="hero-title">
          <picture className="hero-media" aria-hidden="true">
            <source
              media="(max-width: 760px)"
              srcSet="/media/restore-team-hero-960.webp"
              type="image/webp"
            />
            <img
              src="/media/restore-team-hero-1600.webp"
              alt=""
              width="1600"
              height="900"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
          <span className="hero-shade" aria-hidden="true" />
          <div className="hero-baroque-lamps" aria-hidden="true">
            <MiniBaroqueLamp />
            <MiniBaroqueLamp mirrored />
          </div>
          <div className="hero-content">
            <span className="hero-kicker">Restore Massage Therapy</span>
            <h1 id="hero-title">
              {view === "booking" ? text.bookingHeroTitle : text.heroTitle}
            </h1>
            <p>{view === "booking" ? text.bookingHeroBody : text.heroBody}</p>
            <div className="calendar-chip">
              <ShieldCheck size={17} aria-hidden="true" />
              <span>
                <strong>{text.calendarConnected}</strong>
                <small>
                  {schedule?.stale ? text.savedSchedule : text.calendarUpdated}
                  {updatedAt ? ` · ${text.updated} ${updatedAt}` : ""}
                </small>
              </span>
            </div>
          </div>
        </section>

        {view === "booking" ? (
          <section
            className="main-card booking-card"
            aria-labelledby="booking-title"
          >
            <SectionHeading
              id="booking-title"
              icon={<CalendarCheck2 size={22} />}
              title={text.bookingTitle}
              subtitle={text.bookingSubtitle}
              action={
                <button
                  className="refresh-button"
                  type="button"
                  onClick={() => void load(true, true)}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={isRefreshing ? "spin" : ""} size={17} />
                  <span>{text.refresh}</span>
                </button>
              }
            />

            {isLoading && !schedule ? (
              <LoadingState label={text.loading} />
            ) : error && !schedule ? (
              <ErrorState
                title={text.errorTitle}
                body={text.errorBody}
                retry={text.retry}
                onRetry={() => void load(false, true)}
              />
            ) : (
              <div className="booking-flow">
                <div className="start-guide" role="note">
                  <Accessibility size={25} aria-hidden="true" />
                  <div>
                    <strong>{text.flowGuideTitle}</strong>
                    <p>{text.flowGuideBody}</p>
                  </div>
                </div>

                <ProgressRail
                  currentStep={currentStep}
                  labels={[
                    text.stepKind,
                    text.stepTreatment,
                    text.stepDate,
                    text.stepTime,
                    text.stepDetails,
                  ]}
                  ariaLabel={text.progressLabel}
                />

                <BookingStep
                  id="booking-step-1"
                  number="1"
                  title={text.stepKind}
                  hint={text.stepKindHint}
                  prompt={text.stepKindPrompt}
                  complete={Boolean(bookingKind)}
                >
                  <div className="booking-kind-grid">
                    <BookingKindOption
                      icon={<UserRound size={25} />}
                      title={text.kindSingle}
                      body={text.kindSingleBody}
                      resource={text.oneRoom}
                      availability={text.onlineReady}
                      selected={bookingKind === "single"}
                      onSelect={() => chooseBookingKind("single")}
                    />
                    <BookingKindOption
                      icon={<UsersRound size={25} />}
                      title={text.kindCouple}
                      body={text.kindCoupleBody}
                      resource={text.twoRooms}
                      availability={text.onlineReady}
                      selected={bookingKind === "couple"}
                      onSelect={() => chooseBookingKind("couple")}
                    />
                    <BookingKindOption
                      icon={<HeartHandshake size={25} />}
                      title={text.kindFourHands}
                      body={text.kindFourHandsBody}
                      resource={text.twoTherapists}
                      availability={text.onlineReady}
                      selected={bookingKind === "fourHands"}
                      onSelect={() => chooseBookingKind("fourHands")}
                    />
                  </div>
                </BookingStep>

                <BookingStep
                  id="booking-step-2"
                  number="2"
                  title={text.stepTreatment}
                  hint={text.stepTreatmentHint}
                  prompt={text.stepTreatmentPrompt}
                  complete={Boolean(duration)}
                  locked={!bookingKind}
                  lockedMessage={text.chooseKindFirst}
                  baroqueLights
                >
                  <div className={`service-grid${treatments.length === 5 ? " five-durations" : ""}`}>
                    {treatments.map((treatment) => {
                      const selected = treatment.duration === duration;
                      return (
                        <button
                          type="button"
                          className={`service-option${selected ? " selected" : ""}`}
                          aria-pressed={selected}
                          onClick={() => chooseDuration(treatment.duration)}
                          key={treatment.duration}
                        >
                          <span className="option-main">
                            <strong>{treatment.duration}</strong>
                            <small>{text.minutes}</small>
                          </span>
                          <span className="service-price">
                            <small>{text.totalPrice}</small>
                            <b dir="ltr">
                              {formatPrice(treatment.price)}
                            </b>
                          </span>
                          {selected && (
                            <CheckCircle2 size={20} aria-hidden="true" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </BookingStep>

                <BookingStep
                  id="booking-step-3"
                  number="3"
                  title={text.stepDate}
                  hint={text.stepDateHint}
                  prompt={text.stepDatePrompt}
                  complete={dateChosen}
                  locked={!duration}
                  lockedMessage={text.chooseTreatmentFirst}
                >
                  <BookingDatePicker
                    id="booking-calendar-date"
                    schedule={schedule}
                    selectedDate={dateChosen ? selectedDate : ""}
                    locale={locale}
                    now={now}
                    disabled={isCheckingPayment}
                    onSelect={chooseBookingDay}
                  />
                </BookingStep>

                <BookingStep
                  id="booking-step-4"
                  number="4"
                  title={text.stepTime}
                  hint={text.stepTimeHint}
                  prompt={text.stepTimePrompt}
                  complete={Boolean(selectedTime && selectedRoom)}
                  locked={!dateChosen}
                  lockedMessage={text.chooseDateFirst}
                >
                  <div className="time-explanation">
                    <DoorOpen size={21} aria-hidden="true" />
                    <div>
                      <strong>{text.twoRoomsFromCalendar}</strong>
                      <p>{text.busyTimesStayVisible}</p>
                    </div>
                  </div>
                  <div className="legend" aria-label={text.legendTitle}>
                    <span className="legend-free">
                      <CheckCircle2 size={16} /> {text.status.free}
                    </span>
                    <span className="legend-busy">
                      <XCircle size={16} /> {text.status.busy}
                    </span>
                    <span className="legend-closed">
                      <LockKeyhole size={16} /> {text.status.closed}
                    </span>
                    <span className="legend-too-soon">
                      <Clock3 size={16} /> {text.tooSoon}
                    </span>
                    <span className="legend-duration">
                      <AlertCircle size={16} /> {text.notEnoughContinuous}
                    </span>
                  </div>
                  <p className="lead-time-note">
                    <Clock3 size={17} aria-hidden="true" />
                    {text.minimumLeadTime}
                  </p>
                  {bookingKind === "couple" && (
                    <p className="booking-rule-note">
                      <UsersRound size={18} aria-hidden="true" />
                      {text.coupleSelectionHelp}
                    </p>
                  )}
                  {bookingKind === "fourHands" && (
                    <p className="booking-rule-note pending">
                      <HeartHandshake size={18} aria-hidden="true" />
                      {text.fourHandsAvailability}
                    </p>
                  )}
                  {!selectedDay && <DateScheduleStatus isRefreshing={isRefreshing} onRefresh={() => void load(true, true)} labels={text} />}
                  {selectedDay && (
                    <RoomTimePicker
                      day={selectedDay}
                      bookingKind={bookingKind ?? "single"}
                      duration={duration}
                      minimumLeadMinutes={MINIMUM_LEAD_MINUTES}
                      now={now}
                      timeZone={schedule?.timezone ?? "Asia/Jerusalem"}
                      selectedTime={selectedTime}
                      selectedRoom={selectedRoom}
                      onSelect={chooseRoomTime}
                      labels={text}
                    />
                  )}
                </BookingStep>

                <BookingStep
                  id="booking-step-5"
                  number="5"
                  title={text.stepDetails}
                  hint={text.stepDetailsHint}
                  prompt={text.stepDetailsPrompt}
                  complete={
                    clientName.trim().length >= 2 && phoneDigits.length >= 9
                  }
                  locked={!selectedTime || !selectedRoom}
                  lockedMessage={text.chooseRoomFirst}
                >
                  <div className="details-grid">
                    <label>
                      <span>{text.nameLabel}</span>
                      <span className="input-shell">
                        <UserRound size={19} aria-hidden="true" />
                        <input
                          type="text"
                          autoComplete="name"
                          value={clientName}
                          placeholder={text.namePlaceholder}
                          onChange={(event) => {
                            setClientName(event.target.value);
                            setShowPreview(false);
                            setPolicyAccepted(false);
                          }}
                        />
                      </span>
                    </label>
                    <label>
                      <span>{text.phoneLabel}</span>
                      <span className="input-shell" dir="ltr">
                        <Phone size={19} aria-hidden="true" />
                        <input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={clientPhone}
                          placeholder={text.phonePlaceholder}
                          onChange={(event) => {
                            setClientPhone(event.target.value);
                            setShowPreview(false);
                            setPolicyAccepted(false);
                          }}
                        />
                      </span>
                    </label>
                  </div>

                  <div className="privacy-note">
                    <LockKeyhole size={18} aria-hidden="true" />
                    <div>
                      <strong>{text.privacyTitle}</strong>
                      <p>{text.privacyBody}</p>
                      <a
                        href={LEGAL_LINKS.privacy}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${text.privacyPolicy} (${text.opensInNewTab})`}
                      >
                        {text.privacyPolicy}
                      </a>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="primary-action"
                    disabled={!formReady}
                    onClick={openPreview}
                  >
                    <CreditCard size={20} aria-hidden="true" />
                    <span>
                      {formReady ? text.previewBooking : text.completeFields}
                    </span>
                  </button>
                </BookingStep>

                {paymentError && (
                  <div className="payment-error" role="alert">
                    <AlertCircle size={21} aria-hidden="true" />
                    <span>
                      {paymentError === "availability"
                        ? text.availabilityChanged
                        : paymentError === "payment"
                          ? text.paymentUnavailable
                          : paymentError === "booking"
                            ? text.bookingSaveFailed
                            : text.availabilityCheckFailed}
                    </span>
                  </div>
                )}

                {showPreview &&
                  selectedTreatment &&
                  selectedDayLabel &&
                  selectedRoom && (
                    <section className="booking-summary" id="booking-summary">
                      <div className="summary-heading">
                        <BadgeCheck size={25} aria-hidden="true" />
                        <div>
                          <h3>{text.previewTitle}</h3>
                          <p>{text.previewBody}</p>
                        </div>
                      </div>
                      <dl>
                        <div>
                          <dt>{text.kindSummary}</dt>
                          <dd>{bookingKindName}</dd>
                        </div>
                        <div>
                          <dt>{text.serviceSummary}</dt>
                          <dd>
                            <span dir="ltr">{selectedTreatment.duration}</span>{" "}
                            {text.minutes} ·{" "}
                            <span dir="auto">{formatPrice(selectedTreatment.price)}</span>
                          </dd>
                        </div>
                        <div>
                          <dt>{text.dateSummary}</dt>
                          <dd>{selectedDayLabel.date || selectedDay?.label}</dd>
                        </div>
                        <div>
                          <dt>{text.timeSummary}</dt>
                          <dd>{selectedTime}</dd>
                        </div>
                        <div>
                          <dt>{text.roomSummary}</dt>
                          <dd>
                            {selectedRoom === "both"
                              ? text.bothRooms
                              : selectedRoom === "room1"
                                ? text.room1
                                : text.room2}
                          </dd>
                        </div>
                        <div>
                          <dt>{text.clientSummary}</dt>
                          <dd>
                            {clientName.trim()} ·{" "}
                            <span dir="ltr">{clientPhone.trim()}</span>
                          </dd>
                        </div>
                      </dl>

                      {bookingKind === "fourHands" && (
                        <div className="warning-note">
                          <UsersRound size={20} aria-hidden="true" />
                          <p>{text.fourHandsAvailability}</p>
                        </div>
                      )}

                      <div
                        className={`warning-note${checkoutUrl ? "" : " payment-pending"}`}
                      >
                        <AlertCircle size={20} aria-hidden="true" />
                        <div>
                          <strong>
                            {checkoutUrl
                              ? text.notBookedTitle
                              : text.doublePaymentTitle}
                          </strong>
                          <p>
                            {checkoutUrl
                              ? text.notBookedBody
                              : text.doublePaymentBody}
                          </p>
                        </div>
                      </div>

                      <section
                        className={`booking-policy${policyAccepted ? " accepted" : ""}`}
                        aria-labelledby="booking-policy-title"
                      >
                        <div className="booking-policy-heading">
                          <Clock3 size={22} aria-hidden="true" />
                          <div>
                            <h4 id="booking-policy-title">
                              {text.bookingPolicyTitle}
                            </h4>
                            <p>{text.bookingPolicyBody}</p>
                            <nav className="policy-links" aria-label={text.bookingPolicyTitle}>
                              <a
                                href={LEGAL_LINKS.terms}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`${text.termsCancellation} (${text.opensInNewTab})`}
                              >
                                {text.termsCancellation}
                              </a>
                              <a
                                href={LEGAL_LINKS.privacy}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`${text.privacyPolicy} (${text.opensInNewTab})`}
                              >
                                {text.privacyPolicy}
                              </a>
                            </nav>
                          </div>
                        </div>
                        <label className="policy-consent">
                          <input
                            type="checkbox"
                            checked={policyAccepted}
                            onChange={(event) =>
                              setPolicyAccepted(event.target.checked)
                            }
                            aria-describedby="booking-policy-required"
                          />
                          <span>{text.bookingPolicyConsent}</span>
                        </label>
                        {!policyAccepted && (
                          <p
                            className="policy-required"
                            id="booking-policy-required"
                            role="status"
                          >
                            {text.bookingPolicyRequired}
                          </p>
                        )}
                      </section>

                      <div className="summary-actions">
                        <button
                          type="button"
                          className="secondary-action"
                          onClick={() => {
                            setShowPreview(false);
                            setPolicyAccepted(false);
                          }}
                        >
                          {text.editDetails}
                        </button>
                        <button
                          type="button"
                          className="primary-action"
                          disabled={
                            isCheckingPayment || !checkoutUrl || !policyAccepted
                          }
                          onClick={() => void continueToPayment()}
                        >
                          <CreditCard size={20} aria-hidden="true" />
                          {isCheckingPayment
                            ? text.checkingAvailability
                            : checkoutUrl
                              ? policyAccepted
                                ? text.openSecurePayment
                                : text.acceptPolicyToContinue
                              : text.paymentLinkRequired}
                        </button>
                      </div>
                    </section>
                  )}

                {updatedAt && (
                  <div className="updated-line">
                    <ShieldCheck size={16} aria-hidden="true" />
                    <span>
                      {text.updated}: {updatedAt}
                    </span>
                  </div>
                )}
              </div>
            )}
          </section>
        ) : (
          <ScheduleView
            schedule={schedule}
            selectedDate={selectedDate}
            now={now}
            locale={locale}
            freeWindows={freeWindows}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            error={error}
            updatedAt={updatedAt}
            onSelectDay={chooseScheduleDay}
            onRefresh={() => void load(true, true)}
            onRetry={() => void load(false, true)}
          />
        )}
      </main>

      <footer className="site-footer">
        <div className="footer-brand">
          <strong>Restore Massage Therapy</strong>
          <span>© 2026</span>
        </div>
        <nav className="footer-links" aria-label={text.bookingPolicyTitle}>
          <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
            {text.termsCancellation}
          </a>
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
            {text.privacyPolicy}
          </a>
          <a href={LEGAL_LINKS.accessibility} target="_blank" rel="noopener noreferrer">
            {text.accessibilityStatement}
          </a>
        </nav>
      </footer>

      <AccessibilityMenu locale={locale} />
      {isCheckingPayment && (
        <div className="payment-progress-overlay" role="status" aria-live="assertive">
          <div className="payment-progress-card">
            <span className="payment-progress-spinner" aria-hidden="true" />
            <strong>{text.checkingAvailability}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleView({
  schedule,
  selectedDate,
  now,
  locale,
  freeWindows,
  isLoading,
  isRefreshing,
  error,
  updatedAt,
  onSelectDay,
  onRefresh,
  onRetry,
}: {
  schedule: ScheduleResponse | null;
  selectedDate: string;
  now: Date;
  locale: Locale;
  freeWindows: number;
  isLoading: boolean;
  isRefreshing: boolean;
  error: boolean;
  updatedAt: string | null;
  onSelectDay: (date: string) => void;
  onRefresh: () => void;
  onRetry: () => void;
}) {
  const text = copy[locale];
  const selectedDay = schedule?.days.find(day => calendarLabelToIso(day.label) === selectedDate);

  return (
    <section
      className="main-card schedule-card"
      aria-labelledby="schedule-title"
    >
      <SectionHeading
        id="schedule-title"
        icon={<CalendarDays size={22} />}
        title={text.scheduleTitle}
        subtitle={text.scheduleSubtitle}
        action={
          <button
            className="refresh-button"
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={isRefreshing ? "spin" : ""} size={17} />
            <span>{text.refresh}</span>
          </button>
        }
      />

      {isLoading && !schedule ? (
        <LoadingState label={text.loading} />
      ) : error && !schedule ? (
        <ErrorState
          title={text.errorTitle}
          body={text.errorBody}
          retry={text.retry}
          onRetry={onRetry}
        />
      ) : (
        <>
          <BookingDatePicker
            id="schedule-calendar-date"
            schedule={schedule}
            selectedDate={selectedDate}
            now={now}
            locale={locale}
            onSelect={onSelectDay}
          />

          {selectedDay && <div className="availability-summary">
            <DoorOpen size={24} aria-hidden="true" />
            <div>
              <strong>{freeWindows}</strong>
              <span>{text.availableWindows}</span>
            </div>
            <p>{text.roomWindows}</p>
          </div>}

          <div className="legend" aria-label={text.legendTitle}>
            <span className="legend-free">
              <CheckCircle2 size={16} /> {text.status.free}
            </span>
            <span className="legend-busy">
              <XCircle size={16} /> {text.status.busy}
            </span>
            <span className="legend-closed">
              <LockKeyhole size={16} /> {text.status.closed}
            </span>
          </div>

          {!selectedDay ? <DateScheduleStatus isRefreshing={isRefreshing} onRefresh={onRefresh} labels={text} /> : selectedDay.slots.length > 0 ? (
            <div
              className="schedule-grid"
              role="table"
              aria-label={text.scheduleTitle}
            >
              <div className="schedule-grid-head" role="row">
                <span role="columnheader">{text.time}</span>
                <span role="columnheader">{text.room1}</span>
                <span role="columnheader">{text.room2}</span>
              </div>
              {selectedDay.slots.map((slot) => (
                <div className="schedule-row" role="row" key={slot.time}>
                  <time role="cell" dateTime={slot.time} dir="ltr">
                    {slot.time}
                  </time>
                  <RoomStatusCell
                    room={slot.room1}
                    roomName={text.room1}
                    labels={text.status}
                  />
                  <RoomStatusCell
                    room={slot.room2}
                    roomName={text.room2}
                    labels={text.status}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Clock3 size={24} aria-hidden="true" />
              <strong>{text.emptyTitle}</strong>
              <p>{text.emptyBody}</p>
            </div>
          )}

          {updatedAt && (
            <div className="updated-line">
              <ShieldCheck size={16} aria-hidden="true" />
              <span>
                {text.updated}: {updatedAt}
                {schedule?.stale ? ` · ${text.savedSchedule}` : ""}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SectionHeading({
  id,
  icon,
  title,
  subtitle,
  action,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <div className="section-heading">
      <div className="section-heading-copy">
        <span className="section-icon" aria-hidden="true">
          {icon}
        </span>
        <div>
          <h2 id={id}>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

function BookingKindOption({
  icon,
  title,
  body,
  resource,
  availability,
  selected,
  pending = false,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  resource: string;
  availability: string;
  selected: boolean;
  pending?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`booking-kind-option${selected ? " selected" : ""}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="booking-kind-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="booking-kind-copy">
        <strong>{title}</strong>
        <small>{body}</small>
      </span>
      <span className="kind-resource">{resource}</span>
      <span className={`kind-availability${pending ? " pending" : ""}`}>
        {pending ? (
          <Sparkles size={14} aria-hidden="true" />
        ) : (
          <ShieldCheck size={14} aria-hidden="true" />
        )}
        {availability}
      </span>
      {selected && (
        <CheckCircle2 className="kind-selected" size={22} aria-hidden="true" />
      )}
    </button>
  );
}

function ProgressRail({
  currentStep,
  labels,
  ariaLabel,
}: {
  currentStep: number;
  labels: string[];
  ariaLabel: string;
}) {
  return (
    <ol className="progress-rail" aria-label={ariaLabel}>
      {labels.map((label, index) => {
        const number = index + 1;
        const complete = number < currentStep;
        const current = number === currentStep;
        return (
          <li
            className={`${complete ? "complete" : ""}${current ? " current" : ""}`}
            key={label}
          >
            <span>
              {complete ? <Check size={15} aria-hidden="true" /> : number}
            </span>
            <small>{label}</small>
          </li>
        );
      })}
    </ol>
  );
}

function DateScheduleStatus({ isRefreshing, onRefresh, labels }: {
  isRefreshing: boolean;
  onRefresh: () => void;
  labels: (typeof copy)[Locale];
}) {
  if (isRefreshing) return <LoadingState label={labels.loading} />;
  return (
    <div className="empty-state date-schedule-missing" role="status">
      <CalendarDays size={26} aria-hidden="true" />
      <strong>{labels.dateScheduleMissingTitle}</strong>
      <p>{labels.dateScheduleMissingBody}</p>
      <button type="button" className="secondary-action" onClick={onRefresh}>
        <RefreshCw size={19} aria-hidden="true" />{labels.refresh}
      </button>
    </div>
  );
}

function BookingStep({
  id,
  number,
  title,
  hint,
  prompt,
  complete,
  locked = false,
  lockedMessage,
  baroqueLights = false,
  children,
}: {
  id: string;
  number: string;
  title: string;
  hint: string;
  prompt: string;
  complete: boolean;
  locked?: boolean;
  lockedMessage?: string;
  baroqueLights?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={`booking-step${locked ? " locked" : ""}`} id={id}>
      <div className="booking-step-heading">
        <span className="step-number">{number}</span>
        <div>
          <h3>{title}</h3>
          <p>{hint}</p>
        </div>
        {complete && (
          <span className="step-complete" aria-label="Complete">
            <Check size={17} />
          </span>
        )}
      </div>

      {baroqueLights && <ServiceBaroqueLights />}

      {locked ? (
        <div className="locked-step">
          <LockKeyhole size={20} aria-hidden="true" />
          <span>{lockedMessage}</span>
        </div>
      ) : (
        <>
          <div className="step-prompt">
            <strong>{prompt}</strong>
            <ArrowDown size={19} aria-hidden="true" />
          </div>
          {children}
        </>
      )}
    </section>
  );
}

function RoomTimePicker({
  day,
  bookingKind,
  duration,
  minimumLeadMinutes,
  now,
  timeZone,
  selectedTime,
  selectedRoom,
  onSelect,
  labels,
}: {
  day: ScheduleDay;
  bookingKind: BookingKind;
  duration: TreatmentDuration | null;
  minimumLeadMinutes: number;
  now: Date;
  timeZone: string;
  selectedTime: string;
  selectedRoom: RoomSelection | "";
  onSelect: (time: string, room: RoomSelection) => void;
  labels: (typeof copy)[Locale];
}) {
  return (
    <div className="room-time-picker" role="table" aria-label={labels.stepTime}>
      <div className="room-time-head" role="row">
        <span role="columnheader">{labels.time}</span>
        <span role="columnheader">{labels.room1}</span>
        <span role="columnheader">{labels.room2}</span>
      </div>
      {day.slots.map((slot) => {
        const roomAvailability = {
          room1: getRoomBookingAvailability(day, slot.time, "room1", duration, {
            minimumLeadMinutes,
            now,
            timeZone,
          }),
          room2: getRoomBookingAvailability(day, slot.time, "room2", duration, {
            minimumLeadMinutes,
            now,
            timeZone,
          }),
        } satisfies Record<RoomKey, RoomBookingAvailability>;
        const coupleAvailable =
          roomAvailability.room1.available && roomAvailability.room2.available;
        const coupleSelected =
          selectedTime === slot.time && selectedRoom === "both";
        const coupleReason = getCoupleAvailabilityCopy(
          roomAvailability,
          duration,
          labels,
        );
        return (
          <div className="room-time-group" role="rowgroup" key={slot.time}>
            <div className="room-time-row" role="row">
              <time role="cell" dateTime={slot.time} dir="ltr">
                {slot.time}
              </time>
              {bookingKind === "couple" ? (
                <>
                  <RoomAvailabilityCell
                    room={slot.room1}
                    roomName={labels.room1}
                    availability={roomAvailability.room1}
                    duration={duration}
                    labels={labels}
                  />
                  <RoomAvailabilityCell
                    room={slot.room2}
                    roomName={labels.room2}
                    availability={roomAvailability.room2}
                    duration={duration}
                    labels={labels}
                  />
                </>
              ) : (
                (["room1", "room2"] as const).map((roomKey) => (
                  <RoomChoice
                    key={roomKey}
                    roomKey={roomKey}
                    room={slot[roomKey]}
                    availability={roomAvailability[roomKey]}
                    duration={duration}
                    selected={
                      selectedTime === slot.time && selectedRoom === roomKey
                    }
                    onSelect={() => onSelect(slot.time, roomKey)}
                    labels={labels}
                  />
                ))
              )}
            </div>
            {bookingKind === "couple" && (
              <button
                type="button"
                className={`couple-time-choice ${coupleAvailable ? "available" : "unavailable"}${coupleSelected ? " selected" : ""}`}
                disabled={!coupleAvailable}
                aria-pressed={coupleSelected}
                onClick={() => onSelect(slot.time, "both")}
              >
                {coupleAvailable ? (
                  <CheckCircle2 size={19} aria-hidden="true" />
                ) : (
                  <Clock3 size={19} aria-hidden="true" />
                )}
                <span>
                  <strong>
                    {coupleSelected
                      ? labels.selectedBothRooms
                      : coupleAvailable
                        ? labels.selectBothRooms
                        : labels.coupleUnavailable}
                  </strong>
                  <small>{coupleReason}</small>
                </span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RoomChoice({
  roomKey,
  room,
  availability,
  duration,
  selected,
  onSelect,
  labels,
}: {
  roomKey: RoomKey;
  room: RoomState;
  availability: RoomBookingAvailability;
  duration: TreatmentDuration | null;
  selected: boolean;
  onSelect: () => void;
  labels: (typeof copy)[Locale];
}) {
  const roomName = roomKey === "room1" ? labels.room1 : labels.room2;
  const presentation = getRoomAvailabilityCopy(
    room,
    availability,
    duration,
    labels,
    true,
  );

  return (
    <button
      type="button"
      role="cell"
      className={`room-choice ${presentation.visualStatus}${selected ? " selected" : ""}`}
      disabled={!availability.available}
      aria-pressed={selected}
      aria-label={`${roomName}: ${selected ? labels.selected : presentation.title}. ${presentation.detail}`}
      onClick={onSelect}
    >
      {presentation.visualStatus === "free" && (
        <CheckCircle2 size={19} aria-hidden="true" />
      )}
      {presentation.visualStatus === "busy" && (
        <XCircle size={19} aria-hidden="true" />
      )}
      {presentation.visualStatus === "closed" && (
        <LockKeyhole size={18} aria-hidden="true" />
      )}
      {(presentation.visualStatus === "too-soon" ||
        presentation.visualStatus === "duration-blocked") && (
        <Clock3 size={19} aria-hidden="true" />
      )}
      <span>
        <strong>{selected ? labels.selected : presentation.title}</strong>
        <small dir={presentation.interval ? "ltr" : undefined}>
          {presentation.detail}
        </small>
      </span>
    </button>
  );
}

function RoomAvailabilityCell({
  room,
  roomName,
  availability,
  duration,
  labels,
}: {
  room: RoomState;
  roomName: string;
  availability: RoomBookingAvailability;
  duration: TreatmentDuration | null;
  labels: (typeof copy)[Locale];
}) {
  const presentation = getRoomAvailabilityCopy(
    room,
    availability,
    duration,
    labels,
    false,
  );

  return (
    <div
      className={`room-status-cell ${presentation.visualStatus}`}
      role="cell"
      aria-label={`${roomName}: ${presentation.title}. ${presentation.detail}`}
    >
      {presentation.visualStatus === "free" && (
        <CheckCircle2 size={19} aria-hidden="true" />
      )}
      {presentation.visualStatus === "busy" && (
        <XCircle size={19} aria-hidden="true" />
      )}
      {presentation.visualStatus === "closed" && (
        <LockKeyhole size={18} aria-hidden="true" />
      )}
      {(presentation.visualStatus === "too-soon" ||
        presentation.visualStatus === "duration-blocked") && (
        <Clock3 size={19} aria-hidden="true" />
      )}
      <span>
        <strong>{presentation.title}</strong>
        <small dir={presentation.interval ? "ltr" : undefined}>
          {presentation.detail}
        </small>
      </span>
    </div>
  );
}

type RoomVisualStatus =
  "free" | "busy" | "closed" | "too-soon" | "duration-blocked";

function getRoomAvailabilityCopy(
  room: RoomState,
  availability: RoomBookingAvailability,
  duration: TreatmentDuration | null,
  labels: (typeof copy)[Locale],
  interactive: boolean,
): {
  visualStatus: RoomVisualStatus;
  title: string;
  detail: string;
  interval: boolean;
} {
  const requestedMinutes = duration ?? 0;

  if (availability.reason === "busy") {
    const interval =
      room.busyFrom && room.busyUntil
        ? `${room.busyFrom}–${room.busyUntil}`
        : labels.status.busy;
    return {
      visualStatus: "busy",
      title: labels.status.busy,
      detail: interval,
      interval: Boolean(room.busyFrom && room.busyUntil),
    };
  }

  if (availability.reason === "closed") {
    return {
      visualStatus: "closed",
      title: labels.status.closed,
      detail: labels.unavailable,
      interval: false,
    };
  }

  if (availability.reason === "minimum_lead") {
    return {
      visualStatus: "too-soon",
      title: labels.tooSoon,
      detail: labels.minimumLeadShort,
      interval: false,
    };
  }

  if (availability.reason === "insufficient_duration") {
    const detail = availability.maxContinuousMinutes
      ? labels.availableOnlyFor.replace(
          "{minutes}",
          String(availability.maxContinuousMinutes),
        )
      : labels.notEnoughForDuration.replace(
          "{duration}",
          String(requestedMinutes),
        );
    return {
      visualStatus: "duration-blocked",
      title: labels.notEnoughContinuous,
      detail,
      interval: false,
    };
  }

  return {
    visualStatus: "free",
    title: interactive ? labels.selectAvailable : labels.status.free,
    detail: labels.availableForDuration.replace(
      "{duration}",
      String(requestedMinutes),
    ),
    interval: false,
  };
}

function getCoupleAvailabilityCopy(
  rooms: Record<RoomKey, RoomBookingAvailability>,
  duration: TreatmentDuration | null,
  labels: (typeof copy)[Locale],
): string {
  if (rooms.room1.available && rooms.room2.available) {
    return labels.availableForDuration.replace(
      "{duration}",
      String(duration ?? 0),
    );
  }

  const reasons = [rooms.room1.reason, rooms.room2.reason];
  if (reasons.includes("busy")) return labels.coupleRoomBusy;
  if (reasons.includes("closed")) return labels.coupleUnavailable;
  if (reasons.includes("minimum_lead")) return labels.coupleTooSoon;
  return labels.coupleNotEnoughContinuous.replace(
    "{duration}",
    String(duration ?? 0),
  );
}

type StatusLabels = Record<RoomState["status"], string>;

function RoomStatusCell({
  room,
  roomName,
  labels,
}: {
  room: RoomState;
  roomName: string;
  labels: StatusLabels;
}) {
  const interval =
    room.status === "busy" && room.busyFrom && room.busyUntil
      ? `${room.busyFrom}–${room.busyUntil}`
      : null;

  return (
    <div
      className={`room-status-cell ${room.status}`}
      role="cell"
      aria-label={`${roomName}: ${labels[room.status]}${interval ? `, ${interval}` : ""}`}
    >
      {room.status === "free" && <CheckCircle2 size={18} aria-hidden="true" />}
      {room.status === "busy" && <XCircle size={18} aria-hidden="true" />}
      {room.status === "closed" && <LockKeyhole size={18} aria-hidden="true" />}
      <span>
        <strong>{labels[room.status]}</strong>
        {interval && <small dir="ltr">{interval}</small>}
      </span>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="loading-state" aria-live="polite">
      <div className="loading-orbit" aria-hidden="true" />
      <span>{label}</span>
      <div className="skeleton-lines" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

function ErrorState({
  title,
  body,
  retry,
  onRetry,
}: {
  title: string;
  body: string;
  retry: string;
  onRetry: () => void;
}) {
  return (
    <div className="error-state" role="alert">
      <AlertCircle size={28} aria-hidden="true" />
      <strong>{title}</strong>
      <p>{body}</p>
      <button type="button" onClick={onRetry}>
        <RefreshCw size={17} aria-hidden="true" />
        {retry}
      </button>
    </div>
  );
}

export default App;
